import { ReactNode, useState } from 'react';

import { MtxIcon } from '@meilleurtaux/mtp-uikit-react';

import CardPartnerAccessTile from '@/components/CardPartner/components/CardPartnerAccessTile';
import CardPartnerTutorialsTile from '@/components/CardPartner/components/CardPartnerTutorialsTile';
import Image from '@/components/Image/Image';
import ModaleDefaut from '@/components/ModaleDefaut/ModaleDefaut';
import PartnerTutorialsInterstitial from '@/components/PartnerTutorialsInterstitial';
import VideoTutorialsModal from '@/components/VideoTutorialsModal';
import VIDEO_TUTORIALS_MODAL_MODE from '@/enums/videoTutorialsModalMode';
import usePartnerCardTutorials from '@/hooks/usePartnerCardTutorials';

import styles from './styles.module.css';

import type { PartnerTrackingContext } from '@/types/videoTutorials';

interface Props {
  logo: string;
  logoWebp?: string;
  label: string;
  assureur: string;
  link?: string;
  modaleContrats: string[];
  trackingContext?: PartnerTrackingContext;
}

const CardPartner = ({ logo, label, assureur, link, modaleContrats, logoWebp, trackingContext }: Props) => {
  const {
    tutorials,
    partnerName,
    hasTutorials,
    isDemo,
    isTutorialsModalOpen,
    isInterstitialOpen,
    openTutorialsModal,
    closeTutorialsModal,
    closeInterstitial,
    handlePartnerLinkClick,
    handlePartnerLinkKeyDown,
    handlePlay,
    handleHidePartnerTutorials,
    handleHideCheckboxChange,
    handleModalPartnerAccess,
    handleInterstitialPartnerAccessPress,
    handleOpenPlaybackFromInterstitial,
    handleTrackInfo,
  } = usePartnerCardTutorials({ assureur, link, trackingContext });
  const [showModalCardPartner, setShowModalCardPartner] = useState(false);

  const modaleContent: ReactNode = (
    <ul className={styles.contractsModalList}>
      {modaleContrats.map((modaleContrat) => (
        <li key={modaleContrat}>{modaleContrat}</li>
      ))}
    </ul>
  );

  const openInfoModal = () => {
    document.body.classList.add('modal-open');
    handleTrackInfo();
    setShowModalCardPartner(true);
  };

  const closeModal = () => {
    setShowModalCardPartner(false);
  };

  return (
    <>
      {showModalCardPartner && (
        <ModaleDefaut
          title={label}
          content={modaleContent}
          btnLabel="Fermer"
          btnVariant="primary"
          btnSize="large"
          onClickBtn={() => closeModal()}
          btnFullwidth={false}
          onClickClose={() => closeModal()}
          mobileStyle="defaut"
          size="large"
        />
      )}

      <div className={styles.cardPartner} data-equalize="card">
        <div className={styles.cardPartnerHeader}>
          <Image defaultSrc={logo} webpSrc={logoWebp} alt="logo assureur" className={styles.cardPartnerPic} />

          <div className={styles.cardPartnerTitle}>
            <h3>{label}</h3>
            <button
              className={styles.infoButton}
              onClick={openInfoModal}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openInfoModal();
                }
              }}
              tabIndex={0}
              aria-label={`Informations sur ${label}`}
            >
              <MtxIcon name="info-circle" size={24} />
            </button>
          </div>
        </div>

        {link && (
          <div className={styles.cardPartnerActions}>
            {hasTutorials && <CardPartnerTutorialsTile onClick={openTutorialsModal} />}
            <CardPartnerAccessTile
              link={link}
              isDisabled={isDemo}
              onClick={handlePartnerLinkClick}
              onKeyDown={handlePartnerLinkKeyDown}
            />
          </div>
        )}
      </div>

      {link && hasTutorials && (
        <>
          <VideoTutorialsModal
            isOpen={isTutorialsModalOpen}
            onClose={closeTutorialsModal}
            entry={{ mode: VIDEO_TUTORIALS_MODAL_MODE.ALL, tutorials }}
            partnerName={partnerName}
            onPlay={handlePlay}
            onPartnerAccess={handleModalPartnerAccess}
            isPartnerAccessDisabled={isDemo}
          />
          <PartnerTutorialsInterstitial
            isOpen={isInterstitialOpen}
            onClose={closeInterstitial}
            tutorials={tutorials}
            partnerName={partnerName}
            link={link}
            onPlay={handlePlay}
            onPressPartnerAccess={handleInterstitialPartnerAccessPress}
            onHidePartnerTutorials={handleHidePartnerTutorials}
            onHideCheckboxChange={handleHideCheckboxChange}
            onOpenPlayback={handleOpenPlaybackFromInterstitial}
          />
        </>
      )}
    </>
  );
};

export default CardPartner;
